import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Alert,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Chip,
  Divider,
} from '@mui/material';
import {
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Preview as PreviewIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { apiService } from '../../../services/api';
import { OnboardingTemplate, DatabasePreviewSession } from '../../../types/onboarding.types';
import DatabasePreview from './DatabasePreview';

interface AddUserWithTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onUserAdded: () => void;
}

interface AvailableUser {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  fullName: string;
}

const steps = ['Select User', 'Choose Template', 'Set Start Date', 'Preview & Confirm'];

const AddUserWithTemplateModal: React.FC<AddUserWithTemplateModalProps> = ({
  open,
  onClose,
  onUserAdded,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [templates, setTemplates] = useState<OnboardingTemplate[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().add(1, 'day'));
  const [previewSessions, setPreviewSessions] = useState<DatabasePreviewSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadInitialData();
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setActiveStep(0);
    setSelectedUserId('');
    setSelectedTemplateId('');
    setStartDate(dayjs().add(1, 'day'));
    setPreviewSessions([]);
    setError(null);
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [users, templatesData] = await Promise.all([
        apiService.getAvailableUsers(),
        apiService.getOnboardingTemplates(),
      ]);
      setAvailableUsers(users);
      setTemplates(templatesData);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async () => {
    if (!selectedTemplateId || !startDate || !selectedUserId) return;

    try {
      setLoading(true);
      setError(null);
      const preview = await apiService.previewDatabaseInsert({
        userId: selectedUserId,
        templateId: selectedTemplateId,
        startDate: startDate.format('YYYY-MM-DD'),
      });
      setPreviewSessions(preview);
    } catch (err) {
      console.error('Error loading preview:', err);
      setError('Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (activeStep === 2) {
      // Load preview when moving to step 3
      await loadPreview();
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleCreateOnboarding = async () => {
    if (!selectedUserId || !selectedTemplateId || !startDate) return;

    try {
      setCreating(true);
      setError(null);
      
      await apiService.addUserWithTemplate({
        userId: selectedUserId,
        templateId: selectedTemplateId,
        startDate: startDate.format('YYYY-MM-DD'),
      });
      
      onUserAdded();
      onClose();
    } catch (err) {
      console.error('Error creating onboarding:', err);
      setError('Failed to create onboarding sessions');
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    if (!creating) {
      onClose();
    }
  };

  const selectedUser = availableUsers.find(u => u.userId === selectedUserId);
  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  const canProceed = () => {
    switch (activeStep) {
      case 0: return selectedUserId !== '';
      case 1: return selectedTemplateId !== '';
      case 2: return startDate !== null;
      case 3: return previewSessions.length > 0;
      default: return false;
    }
  };

  const renderStepContent = () => {
    if (loading && activeStep !== 3) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select a user to add to the onboarding system using a template.
            </Typography>

            {availableUsers.length === 0 ? (
              <Alert severity="info">
                No users available for onboarding. All active users already have onboarding sessions.
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Select User</InputLabel>
                <Select
                  value={selectedUserId}
                  label="Select User"
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  {availableUsers.map((user) => (
                    <MenuItem key={user.userId} value={user.userId}>
                      <Box>
                        <Typography variant="body1">
                          {user.fullName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose an onboarding template for {selectedUser?.fullName}.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {templates.map((template) => (
                <Card 
                  key={template.id}
                  variant={selectedTemplateId === template.id ? "outlined" : "elevation"}
                  sx={{ 
                    cursor: 'pointer',
                    border: selectedTemplateId === template.id ? 2 : 1,
                    borderColor: selectedTemplateId === template.id ? 'primary.main' : 'divider',
                  }}
                  onClick={() => setSelectedTemplateId(template.id)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6">
                        {template.name}
                      </Typography>
                      {selectedTemplateId === template.id && (
                        <CheckCircleIcon color="primary" />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {template.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip 
                        label={`${template.sessions.length} sessions`} 
                        size="small" 
                        variant="outlined" 
                      />
                      <Chip 
                        label={`${Math.max(...template.sessions.map(s => s.scheduleValue))} ${template.sessions.some(s => s.scheduleType === 'Months') ? 'months' : 'days'}`}
                        size="small" 
                        variant="outlined" 
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Set the start date for {selectedUser?.fullName}'s onboarding using the "{selectedTemplate?.name}" template.
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                minDate={dayjs()}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: 'Sessions will be scheduled from this date onwards, respecting working days and UK bank holidays.'
                  }
                }}
              />
            </LocalizationProvider>

            {selectedTemplate && (
              <Card variant="outlined" sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Template Summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedTemplate.description}
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`${selectedTemplate.sessions.length} sessions`} size="small" />
                    <Chip label="Working days only" size="small" />
                    <Chip label="UK bank holidays avoided" size="small" />
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Calculating session schedule...
                </Typography>
              </Box>
            ) : previewSessions.length > 0 ? (
              <DatabasePreview
                sessions={previewSessions}
                templateName={selectedTemplate?.name || ''}
                userName={selectedUser?.fullName || ''}
                userEmail={selectedUser?.email || ''}
              />
            ) : (
              <Alert severity="error">
                Failed to generate preview. Please go back and try again.
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6">
            Add User with Template
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={creating}>
          Cancel
        </Button>
        
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={creating || loading}>
            Back
          </Button>
        )}
        
        {activeStep < steps.length - 1 ? (
          <Button 
            onClick={handleNext}
            variant="contained"
            disabled={!canProceed() || loading}
          >
            Next
          </Button>
        ) : (
          <Button 
            onClick={handleCreateOnboarding}
            variant="contained"
            disabled={!canProceed() || creating}
          >
            {creating ? 'Creating...' : 'Create Onboarding'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddUserWithTemplateModal;
